export const PAYABLE_FIELDS = Object.freeze([
  'payableAmount',
  'releaseDate',
  'payableType',
  'paymentCycleID',
  'supplierHccID',
  'bankAccountName',
  'reasonCode',
  'description',
]);

export const VENDOR_TYPE = Object.freeze({
  INVOICE: 'invoice',
});

export const JOB_TYPE = Object.freeze({
  INVOICE: 'invoice',
});

export const JOB_STATUS = Object.freeze({
  PENDING: 'pending',
  PENDING_VENDOR: 'pending_vendor',
  PROCESSING: 'processing',
  FAILED: 'failed',
  PENDING_REVIEW: 'pending_review',
  FINISHED: 'finished',
});

export const SUBMISSION_STATUS = Object.freeze({
  CREATED: 'created',
  CREATE_FAILED: 'create_failed',
  REJECTED: 'rejected',
  NOT_SUBMITTED: 'not_submitted',
  // The run stopped during the HRP create call; HRP may have the payable.
  SUBMITTING: 'submitting',
});

export const DELETABLE_JOB_STATUSES = Object.freeze([
  JOB_STATUS.PENDING,
  JOB_STATUS.PENDING_VENDOR,
  JOB_STATUS.PROCESSING,
  JOB_STATUS.FAILED,
  JOB_STATUS.PENDING_REVIEW,
]);

/** Columns shown in the payables table (job-level fields are shown above it). */
export const PAYABLE_TABLE_FIELDS = [
  'bankAccountName',
  'paymentCycleID',
  'payableAmount',
  'releaseDate',
  'description',
];

/** Fixed widths so job list tables share column alignment across sections. */
export const JOB_COLUMN_WIDTHS = Object.freeze({
  vendorName: '14rem',
  createdAt: '9rem',
  updatedBy: '10rem',
  fileCount: '6rem',
  payableCount: '6rem',
  failureReason: '14rem',
  status: '7rem',
  delete: '2.5rem',
});

/** Fixed widths so the payables table scrolls horizontally instead of crushing columns. */
export const PAYABLE_COLUMN_WIDTHS = Object.freeze({
  payableAmount: '8rem',
  releaseDate: '9rem',
  paymentCycleID: '12rem',
  bankAccountName: '14rem',
  description: '18rem',
  submission: '8rem',
  payableId: '11rem',
  actions: '7rem',
});

export const ACCEPT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/plain',
  '.csv',
  '.tsv',
  '.xlsx',
  '.xls',
  '.pdf',
].join(',');

export const GUIDANCE_TEMPLATE = `## Expected payables
Exactly 1 per invoice.

## InvoiceSchema mapping
- account: The field that identifies the employer/client.
  Do NOT use: Bill To, remit-to, or column headers.
- amount: The invoice total field.
  Do NOT use: line-item amounts or subtotals.
- description: Billing period and invoice number.

## Parse
Emit at most one raw line per invoice from the fields above.`;

export const REQUIRED_GUIDANCE_SECTIONS = Object.freeze([
  {
    heading: '## Expected payables',
    tip: 'How many final payables this invoice should produce (e.g. Exactly 1, or one per employer).',
  },
  {
    heading: '## InvoiceSchema mapping',
    tip: 'Where account, amount, and description come from — and what not to use.',
  },
  {
    heading: '## Parse',
    tip: 'How many raw lines to extract from the file before rollup.',
  },
]);

export const VENDOR_INVOICE_ROUTES = Object.freeze({
  LIST: '/finance/vendor-invoices',
  VENDORS: '/finance/vendor-invoices/vendors',
  jobDetail: (jobId) => `/finance/vendor-invoices/${jobId}`,
});

export const VENDOR_INVOICE_QUERY_KEYS = Object.freeze({
  allJobs: ['vendor-invoices'],
  jobsByStatus: (status, { limit, offset } = {}) =>
    limit == null && offset == null
      ? ['vendor-invoices', status]
      : ['vendor-invoices', status, limit, offset],
  job: (jobId) => ['vendor-invoice', jobId],
  vendors: ['vendor-invoice-vendors'],
  preview: (jobId) => ['vendor-invoice-preview', jobId],
});

export const VENDOR_INVOICE_POLLING = Object.freeze({
  ACTIVE_LIST_MS: 5000,
  FINISHED_LIST_MS: 10000,
  DETAIL_WORKING_MS: 2000,
  PREVIEW_MS: 3000,
});

/** Page size for vendor invoice list sections. */
export const JOB_LIST_PAGE_SIZE = 20;

/** Comma-separated statuses for the Processing list bucket. */
export const PROCESSING_LIST_STATUS = `${JOB_STATUS.PENDING},${JOB_STATUS.PROCESSING}`;

/** Fields where comma-delimited values usually mean ambiguous multi-matches. */
const COMMA_UNCERTAIN_FIELDS = new Set([
  'paymentCycleID',
  'bankAccountName',
  'supplierHccID',
  'reasonCode',
]);

export const getFieldStatus = (value, field) => {
  if (value == null || value === '') {
    return 'missing';
  }
  const str = String(value).trim();
  if (!str) {
    return 'missing';
  }
  // Comma-delimited values on match-like fields usually mean the model
  // surfaced multiple possible matches — treat as operator-reviewable.
  if (COMMA_UNCERTAIN_FIELDS.has(field) && str.includes(',')) {
    return 'uncertain';
  }
  return 'complete';
};

/**
 * Drop issue entries for fields that are now complete (stale Dynamo issues
 * after the operator filled the value).
 */
export const clearResolvedIssues = (payable) => {
  const issues = payable?.issues;
  if (!issues || typeof issues !== 'object') {
    return { ...payable, issues: undefined };
  }
  const next = {};
  for (const [field, message] of Object.entries(issues)) {
    if (getFieldStatus(payable?.[field], field) === 'complete') {
      continue;
    }
    const str = String(message ?? '').trim();
    if (str) {
      next[field] = str;
    }
  }
  return {
    ...payable,
    issues: Object.keys(next).length > 0 ? next : undefined,
  };
};

/**
 * A blank releaseDate is not missing: the backend stamps it with the Eastern Time
 * day the payable is filed (approval or resubmit), so the draft never shows a stale date.
 */
export const isReleaseDatePending = (field, value) =>
  field === 'releaseDate' && getFieldStatus(value, field) === 'missing';

export const releaseDatePendingLabel = ({ retryMode = false } = {}) =>
  retryMode ? 'Set on resubmit' : 'Set on approval';

export const isPayableSubmittable = (payable) => {
  const cleaned = clearResolvedIssues(payable);
  if (Object.keys(cleaned.issues ?? {}).length > 0) {
    return false;
  }
  return PAYABLE_FIELDS.every(
    (field) =>
      isReleaseDatePending(field, cleaned?.[field]) ||
      getFieldStatus(cleaned?.[field], field) === 'complete',
  );
};

const RETRYABLE_SUBMISSION_STATUSES = new Set([
  SUBMISSION_STATUS.CREATE_FAILED,
  SUBMISSION_STATUS.REJECTED,
  SUBMISSION_STATUS.NOT_SUBMITTED,
]);

/**
 * The real HRP payable id (`payableIdentifier`) captured on the record's HRP
 * response when the payable was successfully created in HRP. Blank for payables
 * that never created in HRP (draft / not_submitted / rejected / create_failed),
 * so callers should render a placeholder ("-") in that case.
 */
export const getPayableHrpId = (payable) => {
  const id = payable?.response?.data?.payableIdentification?.payableIdentifier;
  return id == null ? '' : String(id);
};

/** True when a finalized VIPAYABLE can be edited and resubmitted to HRP. */
export const isPayableRetryable = (payable) =>
  RETRYABLE_SUBMISSION_STATUSES.has(payable?.submissionStatus);

/**
 * A blank, hand-entered payable: every submittable field present but empty so
 * getFieldStatus reports "missing" and isPayableSubmittable stays false until
 * the operator fills it. Flows through the same submission path as parsed rows.
 */
export const createEmptyPayable = () =>
  Object.fromEntries(PAYABLE_FIELDS.map((field) => [field, '']));

/** Set a field on a payable and clear resolved issues (including the edited field). */
export const applyPayableFieldValue = (payable, field, value) =>
  clearResolvedIssues({ ...payable, [field]: value });

/** Apply a shared field value to every payable in the draft. */
export const applySharedPayableField = (
  payables,
  field,
  value,
  { onlyRetryable = false } = {},
) =>
  (payables ?? []).map((payable) => {
    if (onlyRetryable && !isPayableRetryable(payable)) {
      return payable;
    }
    return applyPayableFieldValue(payable, field, value);
  });

/** True when payables came from Dynamo (post-finalize), not draft/LLM. */
export const isDynamoPayables = (job) => {
  if (job?.payablesSource === 'dynamo') {
    return true;
  }
  if (job?.payablesSource === 'draft') {
    return false;
  }
  // Legacy heuristic when payablesSource is omitted.
  return (
    Array.isArray(job?.payables) &&
    job.payables.some((p) => p?.payableId || p?.submissionStatus)
  );
};

export const sortJobsByUpdatedAt = (jobs = [], { ascending = false } = {}) =>
  [...jobs].sort((a, b) => {
    const aTs = a.updatedAt || a.createdAt || '';
    const bTs = b.updatedAt || b.createdAt || '';
    return ascending ? aTs.localeCompare(bTs) : bTs.localeCompare(aTs);
  });

export const formatIngestionDate = (iso) => {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatJobStatusLabel = (status) => {
  if (status === JOB_STATUS.PENDING) {
    return 'queued';
  }
  if (status === JOB_STATUS.PENDING_VENDOR) {
    return 'vendor identification';
  }
  if (status === JOB_STATUS.PROCESSING) {
    return 'Running';
  }
  return status?.replace(/_/g, ' ') ?? '';
};

export const formatJobTypeLabel = () => 'Invoice';

export const confirmDeleteJob = (vendorName) => {
  const label = vendorName || 'this invoice';
  return window.confirm(`Delete ${label}? This cannot be undone.`);
};

/**
 * Approve payload: full draft payables + rejectedIndices.
 * Server persists every payable with SUBMISSION_STATUS; only non-rejected
 * submittable rows are sent to HRP.
 */
export const buildApprovePayload = (payables, rejectedIndices) => {
  const rejected =
    rejectedIndices instanceof Set ? rejectedIndices : new Set(rejectedIndices);
  return {
    payables: Array.isArray(payables) ? payables : [],
    rejectedIndices: [...rejected],
  };
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll after an async graph/review action until the job visibly changes.
 * Returns the latest fetch after timeout so the UI can still render server
 * state; callers should keep the normal query refetch active for late updates.
 */
export const pollUntilJobUpdated = async (
  fetchJob,
  baseline,
  { maxAttempts = 90, intervalMs = 2000 } = {},
) => {
  const baselineUpdatedAt = baseline?.updatedAt;
  const baselineMessageCount = baseline?.messages?.length ?? 0;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      await sleep(intervalMs);
    }

    const data = await fetchJob();
    if (!data) {
      continue;
    }

    const settled =
      data.status === JOB_STATUS.PENDING_REVIEW ||
      data.status === JOB_STATUS.FINISHED ||
      data.status === JOB_STATUS.FAILED ||
      data.status === JOB_STATUS.PENDING ||
      data.status === JOB_STATUS.PROCESSING;
    const messageCount = data.messages?.length ?? 0;
    const changed =
      data.updatedAt !== baselineUpdatedAt ||
      messageCount > baselineMessageCount ||
      data.status !== baseline?.status;

    if (settled && changed) {
      return data;
    }
  }

  return fetchJob();
};

export const pollUntilVendorConfirmed = async (
  fetchJob,
  baseline,
  { maxAttempts = 90, intervalMs = 2000 } = {},
) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      await sleep(intervalMs);
    }

    const data = await fetchJob();
    if (!data) {
      continue;
    }

    if (
      data.status !== JOB_STATUS.PENDING_VENDOR &&
      data.updatedAt !== baseline?.updatedAt
    ) {
      return data;
    }
  }

  return fetchJob();
};

export const formatCellValue = (value) => {
  if (value == null || value === '') {
    return '';
  }
  const str = String(value).trim();
  if (!str) {
    return '';
  }
  return str;
};

const stringifyToolPayload = (value) => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Apply one review-chat SSE event onto the local message list for live UI.
 * Ignores user_prompt / draft / end / error (callers handle those separately).
 */
export const applyReviewChatEvent = (messages, evt) => {
  if (!evt?.type) {
    return messages;
  }
  const next = Array.isArray(messages) ? [...messages] : [];

  if (evt.type === 'ai_delta' && evt.text) {
    const last = next[next.length - 1];
    if (last?.role === 'agent' && last.streamId === evt.id) {
      next[next.length - 1] = {
        ...last,
        text: `${last.text || ''}${evt.text}`,
        streaming: true,
      };
      return next;
    }
    next.push({
      id: `agent-${evt.id || Date.now()}`,
      streamId: evt.id,
      role: 'agent',
      text: evt.text,
      streaming: true,
    });
    return next;
  }

  if (evt.type === 'tool_call') {
    const last = next[next.length - 1];
    if (last?.streaming) {
      next[next.length - 1] = { ...last, streaming: false };
    }
    next.push({
      id: `tool-call-${evt.id || Date.now()}`,
      role: 'tool_call',
      toolName: evt.name || 'tool',
      text: stringifyToolPayload(evt.args ?? {}),
    });
    return next;
  }

  if (evt.type === 'tool_result') {
    next.push({
      id: `tool-${evt.id || Date.now()}`,
      role: 'tool',
      toolName: evt.name || 'result',
      text: stringifyToolPayload(evt.result ?? ''),
    });
    return next;
  }

  // `guidance` events carry the coach's latest applyable suggestion out-of-band.
  // Captured by the caller into state; nothing to render as a chat bubble.
  if (evt.type === 'guidance') {
    const last = next[next.length - 1];
    if (last?.streaming) {
      next[next.length - 1] = { ...last, streaming: false };
    }
    return next;
  }

  return next;
};
